import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { cloneDeep } from 'lodash'
import scriptjs from 'scriptjs'
import sanitize from 'common/sanitize-html-helper'

import CkEditorTextbox from '@scoir/components-core/dist/components/CkEditorTextbox'
import CharactersRemaining from '@scoir/components-core/dist/components/CharactersRemaining'
import { ErrorBoundary } from '@scoir/error-reporter'

import SectionTable from './components/SectionTable'
import { LabeledCheckMark, LabeledX } from './components/Icons'
import SkeletonPair from './components/SkeletonPair'
import { testRequirementType } from './constants'
import { getCharCount } from './textUtilities'

const useStyles = makeStyles((theme) => ({
  sectionRow: {
    height: 55,
  },
  formControl: {
    margin: `${theme.spacing(1)}px 0`,
    minWidth: 120,
  },
  disabledSelect: {
    color: theme.palette.text.disabled,
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
  },
  thirdLevelSubHeading: {
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(4),
  },
  exceedsError: {
    color: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loadingBox: {
    '& .MuiSkeleton-text': {
      transform: 'unset',
    },
  },
}))

const toolbar = [
  {
    name: 'basicstyles',
    items: [],
  },
  {
    name: 'insert',
    items: ['Link', 'Unlink'],
  },
]

const maxNoteLength = 280

const getId = (id, isFirstYear) => {
  return id + '-' + (isFirstYear ? 'first-year' : 'transfer')
}

const TestSection = ({
  isEditing,
  applicationConfig,
  setApplicationConfig,
  loading,
  showRequiredWarning,
  getStyle,
}) => {
  const [showTestingNotes, setShowTestingNotes] = useState(false)
  const firstYearConfig = applicationConfig.firstYearConfiguration
  const transferConfig = applicationConfig.transferConfiguration
  const initalNotesVal =
    applicationConfig.testingPolicyNotes && applicationConfig.testingPolicyNotes.body
      ? applicationConfig.testingPolicyNotes.body
      : ''
  const showNotesFirstYear =
    firstYearConfig && firstYearConfig.testingPolicy && firstYearConfig.testingPolicy.requirements
      ? firstYearConfig.testingPolicy.requirements === 'optional'
      : false
  const showNotesTransfer =
    transferConfig && transferConfig.testingPolicy && transferConfig.testingPolicy.requirements
      ? transferConfig.testingPolicy.requirements === 'optional'
      : false
  const showNotes = showNotesFirstYear || showNotesTransfer
  const classes = useStyles()
  const [loadedCkEditor, setLoadedCkEditor] = useState(false)
  useEffect(() => {
    scriptjs(CkEditorTextbox.config.CDN, () => {
      setLoadedCkEditor(true)
    })
  }, [])

  const onSelect = (newVal, isFirstYear) => {
    const updatedState = cloneDeep(applicationConfig)

    if (isFirstYear) {
      const config = updatedState.firstYearConfiguration.testingPolicy
        ? updatedState.firstYearConfiguration.testingPolicy
        : {}
      config.requirements = newVal

      handleShowOptionalNotes(newVal, isFirstYear, updatedState)

      if (newVal === 'doNotSend') {
        config.collectScores = false
      }

      updatedState.firstYearConfiguration.testingPolicy = { ...config }
    } else {
      const config = updatedState.transferConfiguration.testingPolicy
        ? updatedState.transferConfiguration.testingPolicy
        : {}
      config.requirements = newVal

      handleShowOptionalNotes(newVal, isFirstYear, updatedState)

      if (newVal === 'doNotSend') {
        config.collectScores = false
      }

      updatedState.transferConfiguration.testingPolicy = { ...config }
    }

    setApplicationConfig(updatedState)
  }

  const handleShowOptionalNotes = (newVal, isFirstYear, updatedState) => {
    const otherConfig = isFirstYear ? updatedState.transferConfiguration : updatedState.firstYearConfiguration
    const otherRequirements = otherConfig.testingPolicy ? otherConfig.testingPolicy.requirements : ''

    if ((newVal === 'doNotSend' || newVal === 'required') && otherRequirements !== 'optional') {
      if (updatedState.testingPolicyNotes) {
        updatedState.testingPolicyNotes.body = null
      }
      setShowTestingNotes(false)
    }
    if (newVal === 'optional') {
      setShowTestingNotes(true)
    }
  }

  const getReadOnlySelect = (_isEnabled, value, isFirstYear) => {
    const displayValue = value ? testRequirementType[value] : 'None'
    return <FormLabel data-testid={getId('test-select-label-testid-', isFirstYear)}>{displayValue}</FormLabel>
  }

  const renderSelect = (config, isFirstYear = false) => {
    const isEnabled = config?.enabled
    const requirementsValue =
      config.testingPolicy && config.testingPolicy.requirements ? config.testingPolicy.requirements : ''
    const hasError = showRequiredWarning && !requirementsValue && isEnabled

    return isEditing ? (
      <FormControl
        required={isEnabled}
        error={hasError}
        fullWidth
        margin="normal"
        variant="outlined"
        size="small"
        className={classes.formControl}
        aria-label={hasError ? 'hasError' : 'noError'}
      >
        <InputLabel className={!isEnabled ? classes.disabledSelect : ''}>Select</InputLabel>
        <Select
          label="Select"
          id={getId('test-select', isFirstYear)}
          data-testid={getId('test-select-testid', isFirstYear)}
          value={requirementsValue}
          defaultValue={''}
          disabled={!isEnabled}
          onChange={(e) => onSelect(e.target.value, isFirstYear)}
        >
          <MenuItem value={'required'}>Requires Scores</MenuItem>
          <MenuItem value={'optional'}>Test-Optional</MenuItem>
          <MenuItem value={'doNotSend'}>Test-Blind</MenuItem>
        </Select>
      </FormControl>
    ) : (
      getReadOnlySelect(isEnabled, requirementsValue, isFirstYear)
    )
  }

  const onToggleCheckbox = (newVal, isFirstYear) => {
    const updatedState = cloneDeep(applicationConfig)

    if (isFirstYear) {
      updatedState.firstYearConfiguration.testingPolicy.collectScores = newVal
    } else {
      updatedState.transferConfiguration.testingPolicy.collectScores = newVal
    }

    setApplicationConfig(updatedState)
  }
  const onTogglePDFCheckbox = (newVal, isFirstYear) => {
    const updatedState = cloneDeep(applicationConfig)

    if (isFirstYear) {
      if (!updatedState.firstYearConfiguration.pdfConfiguration) {
        updatedState.firstYearConfiguration.pdfConfiguration = {}
      }
      updatedState.firstYearConfiguration.pdfConfiguration.hideSatActScores = newVal
    } else {
      if (!updatedState.transferConfiguration.pdfConfiguration) {
        updatedState.transferConfiguration.pdfConfiguration = {}
      }
      updatedState.transferConfiguration.pdfConfiguration.hideSatActScores = newVal
    }
    setApplicationConfig(updatedState)
  }

  const getReadOnlyCheckbox = (isChecked, isEnabled) =>
    isChecked ? (
      <LabeledCheckMark
        enabled={isEnabled}
        getStyle={getStyle}
      />
    ) : (
      <LabeledX
        enabled={isEnabled}
        getStyle={getStyle}
      />
    )

  const renderCheckbox = (config, isFirstYear = false) => {
    const isEnabled = config.enabled
    const isChecked =
      config.testingPolicy && config.testingPolicy.collectScores ? config.testingPolicy.collectScores : false
    const requirementsValue =
      config.testingPolicy && config.testingPolicy.requirements ? config.testingPolicy.requirements : ''
    const checkboxDisabled = requirementsValue === 'required' || requirementsValue === 'optional'
    return isEditing ? (
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              data-testid={getId('test-checkbox', isFirstYear)}
              checked={isChecked}
              name="CollectScores"
              color="primary"
              onChange={(e) => onToggleCheckbox(e.target.checked, isFirstYear)}
              disabled={!isEnabled || !checkboxDisabled}
            />
          }
          label="Yes"
        />
      </FormGroup>
    ) : checkboxDisabled ? (
      getReadOnlyCheckbox(isChecked, isEnabled)
    ) : (
      <Typography>N/A</Typography>
    )
  }
  const renderPDFCheckbox = (config, sectionId, ariaLabel, onChange) => {
    const isEnabled = config.enabled
    const isChecked =
      config.pdfConfiguration && config.pdfConfiguration.hideSatActScores
        ? !config.pdfConfiguration.hideSatActScores
        : true
    const requirementsValue =
      config.testingPolicy && config.testingPolicy.requirements ? config.testingPolicy.requirements : ''
    const checkboxDisabled = requirementsValue === 'doNotSend' || !isEnabled || !requirementsValue
    return isEditing ? (
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxDisabled ? false : isChecked}
              onChange={(e) => onChange(!e.target.checked)}
              name={sectionId}
              color="primary"
              label="Yes"
              aria-label={ariaLabel ?? 'Yes'}
              disabled={!isEnabled || checkboxDisabled}
            />
          }
          label="Yes"
        />
      </FormGroup>
    ) : !checkboxDisabled ? (
      getReadOnlyCheckbox(isChecked, isEnabled)
    ) : (
      <Typography>N/A</Typography>
    )
  }

  const onNotesChange = (sanitizedVal) => {
    const updatedState = cloneDeep(applicationConfig)
    const isError = validateInput(sanitizedVal)

    /* istanbul ignore next */
    const notesConfig = updatedState.testingPolicyNotes ? updatedState.testingPolicyNotes : {}
    notesConfig.body = sanitizedVal
    updatedState.testingPolicyNotes = { ...notesConfig }

    setApplicationConfig(updatedState, isError)
  }

  const validateInput = (val) => {
    const count = getCharCount(val)
    return count > maxNoteLength
  }

  const isError = validateInput(sanitize(initalNotesVal))

  return (
    <SectionTable
      sectionName="SAT/ACT"
      getStyle={getStyle}
    >
      <TableRow
        data-testid="test-section-row"
        key="requirements"
        className={classes.sectionRow}
      >
        <TableCell
          align="left"
          variant="body"
          component="th"
          size="small"
          style={{ color: getStyle() }}
        >
          Standardized test policy
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && (
            <Skeleton
              data-testid="loading-skeleton-testid"
              width={115}
              height={18}
            />
          )}
          {!loading && renderSelect(firstYearConfig, true)}
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && (
            <Skeleton
              data-testid="loading-skeleton-testid"
              width={115}
              height={18}
            />
          )}
          {!loading && renderSelect(transferConfig)}
        </TableCell>
      </TableRow>
      <TableRow
        data-testid="accepts-scoir-test-section-row"
        key="conditional"
        className={classes.sectionRow}
      >
        <TableCell
          align="left"
          variant="body"
          component="th"
          size="small"
          style={{ color: getStyle() }}
        >
          Accept self-reported test scores via Scoir
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && <SkeletonPair />}
          {!loading && renderCheckbox(firstYearConfig, true)}
        </TableCell>
        <TableCell
          align="left"
          size="small"
        >
          {loading && <SkeletonPair />}
          {!loading && renderCheckbox(transferConfig)}
        </TableCell>
      </TableRow>
      <TableRow data-testid="include-sat-act-row">
        <TableCell
          align="left"
          variant="body"
          component="th"
          size="small"
        >
          Include SAT & ACT scores in Application Profile PDFs
        </TableCell>
        <TableCell
          align="left"
          size="small"
          data-testid="firstYearIncludeSatActScoresCell"
        >
          {loading && <SkeletonPair />}

          {!loading &&
            renderPDFCheckbox(
              firstYearConfig,
              'firstYearIncludeSatActScores',
              'include sat and act scores for first year applicants',
              (newVal) => onTogglePDFCheckbox(newVal, true),
            )}
        </TableCell>
        <TableCell
          align="left"
          size="small"
          data-testid="transferIncludeSatActScoresCell"
        >
          {loading && <SkeletonPair />}

          {!loading &&
            renderPDFCheckbox(
              transferConfig,
              'transferIncludeSatActScores',
              'include sat and act scores for transfer applicants',
              (newVal) => onTogglePDFCheckbox(newVal, false),
            )}
        </TableCell>
      </TableRow>

      {(showTestingNotes || (initalNotesVal && initalNotesVal.length > 0) || showNotes) && (
        <React.Fragment>
          <TableRow
            data-testid="test-section-conditions-row"
            key="notes"
            className={classes.sectionRow}
          >
            {loading ? (
              <TableCell>
                <Skeleton
                  data-testid="loader-test-id"
                  className={classes.loadingBox}
                  height={337}
                />
              </TableCell>
            ) : isEditing ? (
              <TableCell colSpan={3}>
                <Typography
                  variant="body2"
                  className={classes.thirdLevelHeading}
                >
                  Are there any conditions associated with your institution's Test-Optional policy?
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.thirdLevelSubHeading}
                >
                  Use this space to provide details to applicants.
                </Typography>
                {loadedCkEditor ? (
                  <React.Fragment>
                    <ErrorBoundary>
                      <CkEditorTextbox
                        initialContent={initalNotesVal}
                        configuration="LINKS"
                        ckEditor={window.CKEDITOR}
                        additionalOptions={{ startupFocus: false, toolbar, versionCheck: false }}
                        onUpdate={(value) => onNotesChange(sanitize(value))}
                      />
                    </ErrorBoundary>

                    <CharactersRemaining
                      textLength={getCharCount(initalNotesVal)}
                      maxChars={maxNoteLength}
                    />

                    {
                      /* istanbul ignore next  */ isError && (
                        <Typography className={classes.exceedsError}>Exceeds character count.</Typography>
                      )
                    }
                  </React.Fragment>
                ) : (
                  <Skeleton
                    data-testid="loader-test-id"
                    className={classes.loadingBox}
                    height={337}
                  />
                )}
              </TableCell>
            ) : (
              <TableCell colSpan={3}>
                <Typography
                  variant="body2"
                  className={classes.thirdLevelHeading}
                >
                  Are there any conditions associated with your institution&apos;s Test-Optional policy?
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.thirdLevelSubHeading}
                >
                  Use this space to provide details to students.
                </Typography>
                {initalNotesVal && initalNotesVal !== '' ? (
                  <Typography
                    data-testid="read-only-text"
                    variant="body1"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(initalNotesVal),
                    }}
                  ></Typography>
                ) : (
                  /* istanbul ignore next  */ <Typography
                    variant="body2"
                    className={classes.disabledSelect}
                  >
                    None
                  </Typography>
                )}
              </TableCell>
            )}
          </TableRow>
        </React.Fragment>
      )}
    </SectionTable>
  )
}

TestSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showRequiredWarning: PropTypes.bool.isRequired,
  getStyle: PropTypes.func.isRequired,
}

export default TestSection
